import React, { useEffect, useRef, useState } from 'react'
import { MoreHorizontal } from 'lucide-react'

/**
 * A small "more" menu (⋯) for the secondary verbs on a row or a detail
 * card — Edit, Delete, the assistant's helpers. Keeps the primary controls
 * (Done, Plan it) visible and everything else one tap away, which is what
 * a 390px row can afford. Same closing rules as PlanItMenu: outside tap,
 * Escape, or a choice.
 */
export interface ActionMenuItem {
  label: string
  icon?: React.ReactNode
  onSelect: () => void | Promise<void>
  danger?: boolean
  disabled?: boolean
  testId?: string
  /** Extra classes on the item, e.g. `sm:hidden` for phone-only entries. */
  className?: string
}

interface ActionMenuProps {
  items: ActionMenuItem[]
  /** Accessible name of the trigger, e.g. "More actions for Call Mom". */
  label: string
  /** Icon-only trigger sized for a list row. */
  compact?: boolean
  align?: 'left' | 'right'
  className?: string
  testId?: string
}

export const ActionMenu: React.FC<ActionMenuProps> = ({ items, label, compact = false, align = 'right', className = '', testId }) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (items.length === 0) return null

  return (
    <div ref={rootRef} className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        data-testid={testId}
        className={compact
          ? 'inline-flex items-center justify-center min-h-touch min-w-touch rounded-md hover:bg-surface-alt text-muted hover:text-primary transition-colors'
          : 'inline-flex items-center justify-center rounded-md border border-soft bg-surface min-h-touch lg:min-h-0 lg:h-9 px-2.5 text-secondary hover:bg-surface-alt hover:text-primary transition-colors'}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label={label}
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full z-30 mt-1 w-52 rounded-lg border border-soft bg-surface p-1 shadow-lg`}
        >
          {items.map((it) => (
            <button
              key={it.label}
              role="menuitem"
              type="button"
              disabled={it.disabled}
              data-testid={it.testId}
              onClick={async () => { setOpen(false); await it.onSelect() }}
              className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm disabled:opacity-60 ${
                it.danger ? 'text-bad hover:bg-bad/10' : 'text-primary hover:bg-surface-alt'
              } ${it.className ?? ''}`}
            >
              {it.icon && <span className={`shrink-0 ${it.danger ? 'text-bad' : 'text-muted'}`}>{it.icon}</span>}
              <span>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default ActionMenu
